import { createRouter, createWebHistory, RouteLocationNormalized } from 'vue-router';
import { store } from './store';

import Bots from './features/bots/views/Bots.vue';
import Flow from './features/bots/views/Flow.vue';
import FlowEditor from './features/bots/views/FlowEditor.vue';
import WebAssistant from './features/webassistant/views/WebAssistant.vue';
import Settings from './features/settings/views/Settings.vue';

import Apps from './features/apps/shared/views/Apps.vue';
import AppModal from './features/apps/shared/components/AppModal.vue';
import AppOverview from './features/apps/shared/components/AppOverview.vue';
import AppConfig from './features/apps/shared/components/AppConfig.vue';

import PrivateApps from './features/privateapps/views/PrivateApps.vue';
import PrivateAppsList from './features/privateapps/views/PrivateAppsList.vue';
import CreatePrivateApp from './features/privateapps/views/CreatePrivateApp.vue';
import EditPrivateApp from './features/privateapps/views/EditPrivateApp.vue';

import Reports from './features/reports/views/Reports.vue';
import FlowsReports from '@/features/reports/views/FlowsReports.vue';
import IntentsReports from '@/features/reports/views/IntentsReports.vue';
import MessagingReports from '@/features/reports/views/MessagingReports.vue';
import ErrorPage from './ErrorPage.vue';

import authService from './authentication/authentication.service';
import UsersInWorkspace from '@/features/settings/views/UsersInWorkspace.vue';
import GeneralSettings from '@/features/settings/views/GeneralSettings.vue';
import Email from '@/features/settings/views/Email.vue';
import EmailTemplate from '@/features/settings/views/EmailTemplate.vue';
import Register from '@/features/registration/views/Register.vue';
import Tasks from '@/features/bots/views/Tasks.vue';
import BotFlows from '@/features/bots/views/BotFlows.vue';
import AnswerEditor from '@/features/bots/views/AnswerEditor.vue';
import Answers from '@/features/bots/views/Answers.vue';
import ActiveLearning from '@/features/bots/views/ActiveLearning.vue';
import { NLP_ENABLED } from '@/store/app/featureTypes';
import Contacts from '@/features/contacts/views/Contacts.vue';
import ContactsList from '@/features/contacts/views/ContactsList.vue';
import ContactAttributes from '@/features/settings/views/ContactAttributes.vue';
import ContactConversationModal from '@/features/contacts/components/ContactConversationModal.vue';
import BusinessHours from '@/features/settings/views/BusinessHours.vue';
import Intents from '@/features/bots/views/Intents.vue';
import UsageReports from '@/features/reports/views/UsageReports.vue';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import PlatformAdmin from '@/features/platformAdmin/PlatformAdmin.vue';
import PlatformAdminListWorkspaces from '@/features/platformAdmin/workspaces/PlatformAdminListWorkspaces.vue';
import EditWorkspace from '@/features/platformAdmin/workspaces/EditWorkspace.vue';
import PlatformAdminListUsers from '@/features/platformAdmin/platformadmins/PlatformAdminListUsers.vue';
import EditWorkspaceUsers from '@/features/platformAdmin/workspaces/EditWorkspaceUsers.vue';
import { ConnectClaim } from '@/authentication/claims';

const generatePageTitle = (pageName: string) => {
  const workspaceName = store.getters.currentWorkspace.name;
  return `${pageName} | ${workspaceName} | Connect`;
};

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: [
    {
      path: '/web-assistant',
      name: 'web-assistant',
      component: WebAssistant,
      meta: {
        title: 'Web Assistant',
      },
    },
    {
      path: '/bots',
      name: 'bots',
      component: Bots,
      meta: {
        title: 'Bot Studio',
      },
      children: [
        {
          path: 'tasks',
          name: 'bots-tasks',
          component: Tasks,
          meta: {
            title: 'Tasks',
          },
        },
        {
          path: 'flows',
          name: 'bots-flows',
          component: BotFlows,
          meta: {
            title: 'Flows',
          },
        },
        {
          path: 'faqs',
          name: 'bots-answers',
          component: Answers,
          meta: {
            title: 'FAQs',
          },
        },
        {
          path: 'active-learning',
          name: 'bots-active-learning',
          component: ActiveLearning,
          meta: {
            title: 'Active Learning',
          },
        },
        {
          path: 'intents',
          name: 'bots-intents',
          component: Intents,
          meta: {
            title: 'Intents',
          },
        },
      ],
    },
    {
      path: '/bots/faqs/:id',
      name: 'bots-answer-editor',
      component: AnswerEditor,
      meta: {
        title: 'FAQs',
      },
    },
    {
      path: '/bots/flows/:flowId',
      component: Flow,
      children: [
        {
          path: '',
          name: 'bots-flow-designer',
          component: FlowEditor,
          meta: {
            title: 'Flows',
          },
        },
      ],
      props: true,
    },
    {
      path: '/apps',
      name: 'apps',
      component: Apps,
      meta: {
        title: 'App Store',
      },
      children: [
        {
          path: ':id',
          name: 'app',
          component: AppModal,
          children: [
            {
              path: 'overview',
              name: 'app-overview',
              component: AppOverview,
            },
            {
              path: 'config',
              name: 'app-config',
              component: AppConfig,
            },
          ],
        },
      ],
    },
    {
      path: '/your-apps',
      component: PrivateApps,
      meta: {
        title: 'Your Apps',
      },
      children: [
        {
          path: '',
          name: 'private-apps-list',
          component: PrivateAppsList,
        },
        {
          path: 'add',
          name: 'private-apps-create',
          component: CreatePrivateApp,
        },
        {
          path: ':id',
          name: 'private-apps-edit',
          component: EditPrivateApp,
        },
      ],
    },
    {
      path: '/contacts',
      component: Contacts,
      meta: {
        title: 'Contacts',
      },
      children: [
        {
          path: '',
          name: 'contacts-list',
          component: ContactsList,
          children: [
            {
              path: ':id',
              name: 'contact-view',
              component: ContactConversationModal,
              props: true,
            },
          ],
        },
      ],
    },

    {
      path: '/reports',
      name: 'reports',
      component: Reports,
      meta: {
        title: 'Reports',
      },
      children: [
        {
          path: 'overview',
          name: 'reports-overview',
          component: MessagingReports,
          meta: {
            title: 'Reports | Overview',
          },
        },
        {
          path: 'usage',
          name: 'reports-usage',
          component: UsageReports,
          meta: {
            title: 'Reports | Usage',
          },
        },
        {
          path: 'flows',
          name: 'reports-flows',
          component: FlowsReports,
          meta: {
            title: 'Reports | Flows',
          },
        },
        {
          path: 'intents',
          name: 'reports-intents',
          component: IntentsReports,
          meta: {
            title: 'Reports | Intents',
          },
        },
      ],
    },
    {
      path: '/settings',
      name: 'settings',
      component: Settings,
      meta: {
        title: 'Settings',
      },
      children: [
        {
          path: 'general',
          name: 'general-settings',
          component: GeneralSettings,
          meta: {
            title: 'General Settings',
          },
        },
        {
          path: 'teammates',
          name: 'teammates',
          component: UsersInWorkspace,
          meta: {
            title: 'Teammates',
          },
        },
        {
          path: 'email',
          name: 'email',
          component: Email,
          meta: {
            title: 'Email',
          },
        },
        {
          path: 'attributes',
          name: 'contact-attributes',
          component: ContactAttributes,
          meta: {
            title: 'Contact Attributes',
          },
        },
        {
          path: 'business-hours',
          name: 'business-hours',
          component: BusinessHours,
          meta: {
            title: 'Business Hours',
          },
        },
      ],
    },
    {
      path: '/settings/email/add',
      name: 'email-template-create',
      component: EmailTemplate,
    },
    {
      path: '/settings/email/:templateId',
      name: 'email-template-edit',
      component: EmailTemplate,
    },
    {
      path: '/platform-admin',
      component: PlatformAdmin,
      meta: {
        title: 'Platform Admin',
      },
      beforeEnter: () => isPlatformAdmin(),
      children: [
        {
          path: 'workspaces',
          name: 'platform-admin-workspaces-list',
          component: PlatformAdminListWorkspaces,
          meta: {
            description: 'List of all workspaces in environment',
          },
        },
        {
          path: 'workspaces/:id',
          name: 'platform-admin-workspace-edit',
          component: EditWorkspace,
          meta: {
            description: 'View apps for workspace and update features',
          },
        },
        {
          path: 'workspaces/:id/users',
          name: 'platform-admin-workspace-edit-users',
          component: EditWorkspaceUsers,
          meta: {
            description: 'All users in this workspace',
          },
        },
        {
          path: 'users',
          name: 'platform-admin-users-list',
          component: PlatformAdminListUsers,
          meta: {
            description: 'Manage platform admin users',
          },
        },
      ],
    },
    {
      path: '/error',
      name: 'error',
      component: ErrorPage,
      meta: {
        title: 'Error',
      },
    },
    {
      path: '/register/:token',
      name: 'register',
      component: Register,
      meta: {
        title: 'Register',
      },
    },
    {
      path: '/',
      name: 'root',
      component: WebAssistant,
      meta: {
        title: 'Web Assistant',
      },
    },
  ],
});

export function registerRouterHooks() {
  router.beforeEach((to, from, next) => {
    // if not authenticated, only allow access to registration page
    if (!authService.isAuthenticated()) {
      if (to.path.startsWith('/register')) {
        return next();
      }

      authService.login();
      return next(false);
    }

    if (!authService.isConnectUser()) {
      if (to.path === '/error') {
        return next();
      } else if (from.path === '/error') {
        return next(false);
      }

      store.dispatch(HANDLE_SERIOUS_ERROR, 'User does not have access to Connect').then();
      return next(false);
    }

    // if logged in and registration page is requested, log them out
    if (to.path.startsWith('/register')) {
      authService.logout();
      return next(false);
    }

    if (
      (to.path.endsWith('/bots/faqs') ||
        to.path.endsWith('/bots/active-learning') ||
        to.path.endsWith('/bots/intents')) &&
      !store.getters.isWorkspaceFeatureEnabled(NLP_ENABLED)
    ) {
      return next(from as any);
    }

    updatePageTitle(to);

    // allow anything else
    next();
  });
}

function isPlatformAdmin() {
  return authService.getClaim(ConnectClaim.IsPlatformAdmin);
}

export function updatePageTitle(route?: RouteLocationNormalized) {
  route = route || router.currentRoute.value;
  if (!route) return;

  const nearestPageWithTitle = route.matched
    .slice()
    .reverse()
    .find((r) => r.meta && r.meta.title);

  if (nearestPageWithTitle) {
    document.title = generatePageTitle(<string>nearestPageWithTitle.meta.title);
  }
}

export default router;
